// React imports
import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// Sections components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MediaCard from "components/Cards/BlogCards/CenteredBlogCard/MediaCard";
import { Skeleton } from "@mui/material";

// Icons
import { Delete, Favorite } from "@mui/icons-material";

// Components imports
import SortablePhotoAlbum from "components/SortablePhotoAlbum/SortablePhotoAlbum";
import SortableVideoAlbum from "components/SortablePhotoAlbum/SortableVideoAlbum";

// Modal imports
import { ModalProvider } from "components/SortablePhotoAlbum/admin/ModalProvider";
import EditView from "components/SortablePhotoAlbum/admin/EditView";

import axios from "axios";
import SEO from "components/SEO";
import { useTranslation } from "react-i18next";

const breakpoints = [480, 768, 1024, 1280, 1600, 1920, 2560];

/*
 * PhotoViewer is responsible for fetching the photos and displaying them using
 * SortablePhotoAlbum.
 * Currently also responsible for handling selection and removal of images.
 */
function PhotoViewer({ album, refreshFlag }) {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [displayImages, setDisplayImages] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  useEffect(() => {
    async function fetchImages() {
      setLoading(true);
      setPhotos([]);

      // Get the album images from the backend
      const photoRequest = await axios.get(`${process.env.REACT_APP_BACKEND}/images/${album}`);
      const photoData = photoRequest.data;

      /*
       * imageLink is responsible for generating the source that is used for imgix
       * If no newWidth and newHeight, then it returns the source image,
       * If they are provided, then it uses imgix to generate the dimension and returns that link.
       */
      function imageLink(path, width, height, extension, newWidth, newHeight) {
        if (newWidth && newHeight)
          return `${process.env.REACT_APP_IMGIX}/${album}/${path}_${width}x${height}.${extension}?w=${newWidth}&h=${newHeight}&fit=max&auto=format&dpr=2`;
        return `${process.env.REACT_APP_IMGIX}/${album}/${path}_${width}x${height}.${extension}`;
      }

      const parsedPhotos = photoData.combinedData
        .map((file) => {
          if (!file || !file.name) return null;

          const matcher = file.name.match("^(.*)_(\\d+)x(\\d+)\\.(.+)$");

          if (!matcher) {
            console.warn("Skipping unmatched file:", file.name);
            return null;
          }

          const path = matcher[1];
          const width = Number.parseInt(matcher[2], 10);
          const height = Number.parseInt(matcher[3], 10);
          const extension = matcher[4];

          // Build the photo object with all the processed data.
          return {
            path: album + "/" + file.name,
            id: file.database_id,
            src: imageLink(path, width, height, extension),
            width,
            height,
            srcSet: breakpoints.map((breakpoint) => {
              const resizedHeight = Math.round((height / width) * breakpoint);
              return {
                src: imageLink(path, width, height, extension, breakpoint, resizedHeight),
                width: breakpoint,
                height: resizedHeight,
              };
            }),
            selected: false,
            display_order: file.display_order,
            roomId: file.roomId,
          };
        })
        .filter(Boolean);

      setPhotos(parsedPhotos);
      setLoading(false);
    }

    async function fetchVideos() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND}/videos`);
        const thumbnailRes = await axios.get(`${process.env.REACT_APP_BACKEND}/videos/display`);
        const parsed = res.data;
        setVideos(parsed);
        setThumbnail(thumbnailRes.data.thumbnail);
      } catch (err) {
        console.error("Error fetching videos:", err);
      } finally {
        setLoading(false);
      }
    }

    if (album == "interior" || album == "exterior") fetchImages();

    if (album == "video") fetchVideos();
  }, [album, refreshFlag]);

  //Get the display images for SEO
  useEffect(() => {
    async function fetchDisplayImages() {
      try {
        const [interiorRes, exteriorRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND}/images/display/interior`),
          axios.get(`${process.env.REACT_APP_BACKEND}/images/display/exterior`),
        ]);

        setDisplayImages({
          interior: interiorRes.data,
          exterior: exteriorRes.data,
        });
      } catch (err) {
        console.error("Error fetching display images:", err);
      }
    }

    fetchDisplayImages();
  }, []);

  const seoData = useMemo(() => {
    let seoImage;
    if (displayImages) {
      if (displayImages.interior && album === "interior") {
        seoImage = displayImages.interior.image_path;
      } else if (displayImages.exterior && album === "exterior") {
        seoImage = displayImages.exterior.image_path;
      }
    }
    //Fall back is the first image
    if (!seoImage || seoImage.includes("placehold")) {
      seoImage = photos[0]?.src + "?w=480&h=360";
    }

    if (album === "interior") {
      return {
        title: t("Interior Photos of Four Roses Villa in Alvor, Portugal"),
        description: t(
          "Explore the interior of our villa rental in Alvor, Portugal. View spacious rooms, elegant design, and fully equipped living spaces at Four Roses."
        ),
        image: seoImage,
        structuredData: {
          "@type": "CollectionPage",
          name: t("Interior photos of Four Roses in Alvor, Portugal"),
          description: t(
            "Explore the interior of our villa rental in Alvor, Portugal. View spacious rooms, elegant design, and fully equipped living spaces at Four Roses."
          ),
        },
      };
    }

    if (album === "exterior") {
      return {
        title: t("Exterior Photos of Four Roses Villa in Alvor, Portugal"),
        description: t(
          "View the exterior of our villa rental in Alvor, Portugal. Discover outdoor spaces, the property surroundings, and the beautiful setting of Four Roses."
        ),
        image: seoImage,
        structuredData: {
          "@type": "CollectionPage",
          name: t("Exterior Photos of Four Roses Villa in Alvor, Portugal"),
          description: t(
            "View the exterior of our villa rental in Alvor, Portugal. Discover outdoor spaces, the property surroundings, and the beautiful setting of Four Roses."
          ),
        },
      };
    }

    if (album === "video") {
      return {
        title: t("Videos of Four Roses Villa in Alvor, Portugal"),
        description: t(
          "Watch videos of our villa rental in Alvor, Portugal and explore the property, amenities, and surroundings at Four Roses."
        ),
        image: thumbnail,
        structuredData: {
          "@type": "CollectionPage",
          name: t("Videos of Four Roses Villa in Alvor, Portugal"),
          description: t(
            "Watch videos of our villa rental in Alvor, Portugal and explore the property, amenities, and surroundings at Four Roses."
          ),
        },
      };
    }

    return {
      title: t("Villa Gallery in Alvor, Portugal | Four Roses"),
      description: t(
        "Browse photos and media of our villa rental in Alvor, Portugal at Four Roses."
      ),
      image: seoImage,
      structuredData: {
        "@type": "CollectionPage",
        name: t("Villa Gallery in Alvor, Portugal | Four Roses"),
        description: t(
          "Browse photos and media of our villa rental in Alvor, Portugal at Four Roses."
        ),
      },
    };
  }, [album, photos, t]);

  const handleDeletePhotos = async () => {
    const selectedPhotos = photos.filter((photo) => photo.selected);

    if (selectedPhotos.length < 1) return;

    //TODO:: Make it a model
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedPhotos.length} photos?`
    );

    if (!confirmDelete) return;

    // Get all the selected images ids
    const toDeleteIds = selectedPhotos.map((photo) => photo.id);

    // Send request to backend
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND}/images/deleteMany`, {
        ids: toDeleteIds,
      });

      // Update UI only if all deletes succeeded
      setPhotos((prev) => prev.filter((photo) => !photo.selected));
    } catch (err) {
      console.error("Error deleting images:", err);
      alert("Some images could not be deleted.");
    }
  };

  const handleDeleteVideos = async () => {
    const selectedVideos = videos.filter((video) => video.selected);

    if (selectedVideos.length < 1) return;

    //TODO:: Make it a model
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedVideos.length} videos?`
    );

    if (!confirmDelete) return;

    // Get all the selected images ids
    const toDeleteIds = selectedVideos.map((video) => video.id);

    // Send request to backend
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND}/videos/deleteMany`, {
        ids: toDeleteIds,
      });

      // Update UI only if all deletes succeeded
      setVideos((prev) => prev.filter((video) => !video.selected));
    } catch (err) {
      console.error("Error deleting videos:", err);
      alert("Some videos could not be deleted.");
    }
  };

  const setDisplayPhoto = async (id) => {
    try {
      await axios
        .put(`${process.env.REACT_APP_BACKEND}/images/display/${album}/${id}`)
        .then((res) => console.log(res));
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  const setDisplayVideo = async (id) => {
    try {
      await axios
        .put(`${process.env.REACT_APP_BACKEND}/videos/display/${id}`)
        .then((res) => console.log(res));
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <MKBox sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <SEO
        title={seoData.title}
        description={seoData.description}
        image={seoData.image}
        type="website"
        structuredData={seoData.structuredData}
      />

      {/* If one or more photo/video is selected */}
      {(photos.filter((photo) => photo.selected).length > 0 ||
        videos.filter((video) => video.selected).length > 0) && (
        <MKBox sx={{ mb: 2, display: "flex" }}>
          <MKButton
            size="medium"
            color="secondary"
            variant="gradient"
            sx={{ mr: 1, maxWidth: "200px" }}
            onClick={() => {
              if (photos.length > 0)
                setPhotos((prev) => prev.map((img) => ({ ...img, selected: true })));
              else setVideos((prev) => prev.map((img) => ({ ...img, selected: true })));
            }}
          >
            Select all photos
          </MKButton>
          <MKButton
            size="medium"
            color="secondary"
            variant="gradient"
            sx={{ maxWidth: "200px" }}
            onClick={() => {
              if (photos.length > 0)
                setPhotos((prev) => prev.map((img) => ({ ...img, selected: false })));
              else setVideos((prev) => prev.map((img) => ({ ...img, selected: false })));
            }}
          >
            Unselect all photos
          </MKButton>
          {/* If exactly one video OR photo is selected */}
          {(photos.filter((photo) => photo.selected).length === 1 ||
            videos.filter((video) => video.selected).length === 1) &&
            (() => {
              const selectedPhoto = photos.find((photo) => photo.selected);
              const selectedVideo = videos.find((video) => video.selected);
              return (
                <MKButton
                  size="medium"
                  color="success"
                  variant="gradient"
                  sx={{ marginLeft: "auto", maxWidth: "250px" }}
                  onClick={() => {
                    if (photos.length > 0) setDisplayPhoto(selectedPhoto?.id);
                    else setDisplayVideo(selectedVideo?.id);
                  }}
                >
                  <Favorite sx={{ mr: 1 }} /> Make display photo
                </MKButton>
              );
            })()}
          <MKButton
            size="medium"
            color="error"
            variant="gradient"
            sx={{ marginLeft: "auto", maxWidth: "250px" }}
            onClick={() => {
              if (photos.length > 0) handleDeletePhotos();
              else handleDeleteVideos();
            }}
          >
            <Delete sx={{ mr: 1 }}>Delete</Delete> Delete Selected (
            {photos.length > 0
              ? photos.filter((photo) => photo.selected).length
              : videos.filter((video) => video.selected).length}
            )
          </MKButton>
        </MKBox>
      )}

      <MKBox sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <MKBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" width="100%" height={200} animation="wave" />
            ))}
          </MKBox>
        ) : album != "video" ? (
          <ModalProvider>
            <SortablePhotoAlbum photos={photos} setPhotos={setPhotos} album={album} />
            <EditView />
          </ModalProvider>
        ) : (
          <SortableVideoAlbum videos={videos} setVideos={setVideos} />
        )}
      </MKBox>
      <MediaCard
        toDisplay={["exterior", "interior", "video"].filter((item) => item !== album)}
        containsHeader={true}
      />
    </MKBox>
  );
}

PhotoViewer.propTypes = {
  album: PropTypes.string.isRequired,
  refreshFlag: PropTypes.bool,
};

export default PhotoViewer;
